import {
  CoachPortalInAppAssessment,
  CoachPortalSession,
  CoachSessionStudent,
  InAppAssessmentResultSubmission,
  SessionStatus,
} from '@airreading/common/data';

/**
 * TODO: per-format durations land with the stats ticket. The server already measures
 * `progress_monitor_sounds` over 30 seconds rather than 60.
 */
export const DEFAULT_ASSESSMENT_DURATION_SECONDS = 60;

/**
 * One student row in the assessment dropdown, paired with the runnable contents for the selected
 * timed-content kind. Progress monitor can have several (passage and/or word list); GLA and fluency
 * check typically have one.
 */
export interface InAppAssessmentStudent {
  student: CoachSessionStudent;
  displayName: string;
  gradeLabel: string | null;
  assessments: CoachPortalInAppAssessment[];
}

export function canRunInAppAssessments(session: CoachPortalSession): boolean {
  return session.status !== SessionStatus.cancelled && (session.inAppAssessments ?? []).length > 0;
}

/** Words are delimited by any run of whitespace, so line breaks never fuse two words together. */
export function tokenizePassage(passage: string): string[] {
  return passage.split(/\s+/).filter((token) => token.length > 0);
}

export function formatCountdown(secondsRemaining: number): string {
  const clamped = Math.max(0, Math.trunc(secondsRemaining));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export type AssessmentPhase =
  | 'idle'
  | 'running'
  | 'paused'
  | 'confirmingEarlyFinish'
  | 'earlyFinishReady'
  | 'timeUp'
  | 'submitting'
  | 'done';

/** True after Start until the attempt is reset or submitted. */
export function isInAppAssessmentAttemptActive(phase: AssessmentPhase): boolean {
  return phase !== 'idle' && phase !== 'done';
}

/** Errors can only be marked on words the student is treated as having read. */
export function canMarkInAppAssessmentError(wordIndex: number, lastWordIndex: number | null): boolean {
  return lastWordIndex === null || wordIndex <= lastWordIndex;
}

/** Last word must cover every already-marked error. */
export function canSelectInAppAssessmentLastWord(wordIndex: number, markedWordIndexes: readonly number[]): boolean {
  return markedWordIndexes.every((index) => index <= wordIndex);
}

export interface InAppAssessmentStats {
  totalWordsRead: number;
  incorrectWordsCount: number;
  totalSecondsSpent: number;
  finishedEarly: boolean;
}

export function buildInAppAssessmentResultSubmission({
  words,
  markedWordIndexes,
  lastWordIndex,
  secondsRemaining,
  durationSeconds = DEFAULT_ASSESSMENT_DURATION_SECONDS,
  finishedEarly,
}: {
  words: string[];
  markedWordIndexes: readonly number[];
  lastWordIndex: number | null;
  secondsRemaining: number;
  durationSeconds?: number;
  finishedEarly: boolean;
}): InAppAssessmentResultSubmission {
  const totalWordsRead = finishedEarly ? words.length : (lastWordIndex ?? 0) + 1;
  const incorrectWords = markedWordIndexes
    .filter((index) => index < totalWordsRead)
    .map((index) => words[index])
    .filter((word): word is string => word != null);
  return {
    incorrectWords,
    incorrectWordsCount: incorrectWords.length,
    totalWordsRead,
    // Time-up uses the full measured window; early finish uses elapsed seconds.
    totalSecondsSpent: finishedEarly ? Math.max(1, durationSeconds - secondsRemaining) : durationSeconds,
    finishedEarly,
    assessmentFinished: true,
  };
}
